// Lightweight inline SVG icon components for maximum performance (no external deps)
// Icons use currentColor so parent element's color (hover/active) automatically applies.
// Optional size prop allows flexible sizing without repeating components.

const baseProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

// Brand logo (inline SVG). Height is configurable; width is derived from aspect ratio (179:65).
export const IconLogo = ({
  height = 32,
  className = '',
  brandColor = '#0C1854',
  accentColor = '#118584',
  accentOpacity = 0.5,
  ...props
}) => {
  const aspect = 179 / 65;
  const width = Math.round(height * aspect);
  return (
    <svg
      role="img"
      aria-label="NetX Logo"
      viewBox="0 0 179 65"
      width={width}
      height={height}
      className={`nav-brand-logo ${className}`.trim()}
      {...props}
    >
      <defs>
        <path id="logoPath" d="M0 0h32.2v34.2H0z" />
      </defs>
      <g fill="none" fillRule="evenodd" style={{ color: brandColor }}>
        <g fill={accentColor} fillOpacity={accentOpacity} fillRule="nonzero">
          <path d="M136.3 15.5 136.3 1 134.6 1.5 134.6 14ZM142.3 21.5 142.3 0 140.5 0.3 140.5 20 142.2 21.4 142.3 21.5ZM152.5 20 152.5 0.3 150.8 0.1 150.8 21.4 152.5 20ZM146.5 25.7 147.4 25 147.4 -0.7 145.6 -0.7 145.6 25ZM157.5 14.1 157.5 1.4 156 1 156 15.5 157.7 14.1 157.5 14.1ZM131.2 10.3 131.2 3.5 129.5 4.3 129.5 9ZM163.5 9.7 163.5 4.3 161.8 3.5 161.8 11.2 163.5 9.7ZM136.3 49.5 136.3 64 134.6 63.5 134.6 51ZM142.2 43.6 142.2 64.9 140.5 64.6 140.5 45ZM152.5 45 152.5 64.7 150.8 64.9 150.8 43.6ZM145.6 39.2 145.6 64.9 147.4 64.9 147.4 39.2 146.5 38.5ZM157.6 51 157.6 63.6 155.9 64 155.9 49.5 157.6 50.9ZM131.2 54.7 131.2 61.5 129.5 60.7 129.5 56ZM163.5 55.3 163.5 60.7 161.8 61.5 161.8 53.8 163.5 55.3ZM129.5 42.7 115 42.7 115.5 44.4 128 44.4ZM135.5 36.7 114 36.7 114.3 38.5 134 38.5 135.4 36.8 135.5 36.7ZM134 26.6 114.3 26.6 114.1 28.3 135.4 28.3ZM139.7 32.6 139 31.7 113.3 31.7 113.3 33.5 139 33.5ZM128.1 20.6 115.4 20.6 115 22.4 129.5 22.4 128.1 20.7ZM124.3 47.9 117.5 47.9 118.3 49.6 123 49.6ZM123.7 15.6 118.3 15.6 117.5 17.3 125.2 17.3ZM162.7 42.8 177.2 42.8 176.7 44.5 164 44.5 162.6 42.8ZM157.6 36.8 178.9 36.8 178.6 38.6 159 38.6 157.6 36.9 157.6 36.8ZM159 26.6 178.7 26.6 178.9 28.3 157.6 28.3 159 26.6ZM153.2 33.5 178.9 33.5 178.9 31.7 153.2 31.7 152.5 32.6ZM164.9 20.7 177.6 20.7 178 22.4 163.5 22.4ZM168.7 47.9 175.5 47.9 174.7 49.6 170 49.6ZM169.3 15.6 174.7 15.6 175.5 17.3 167.8 17.3 169.3 15.6Z" />
        </g>
        <g transform="translate(0 15.5)">
          <mask id="logoMask" fill="#fff">
            <use xlinkHref="#logoPath" />
          </mask>
          <path fill={brandColor} mask="url(#logoMask)" d="M28.2 28.6 4.6 0H0v34.2h4V5.6l23.6 28.6h4.6V0h-4z" />
        </g>
        <path d="M40 49.7h32.2v-4H40v4Zm0-30.2h32.2v-4H40v4Zm1 14.9h25.8l2-2-2-2H41v4Zm35.5-18.9v4h14.1v30.2h4V19.5h14.1v-4H76.5Z" fill={brandColor} />
        <g className="logo-x">
          <path className="logo-x-base" d="m163 13.6 2.8 2.8-16.3 16.3L165.8 49l-2.8 2.8-16.3-16.3-16.3 16.3-2.8-2.8 16.3-16.3-16.3-16.3 2.8-2.8 16.3 16.3L163 13.6Z" fill={brandColor} />
        </g>
      </g>
    </svg>
  );
};

// Compact N-lettermark only (for collapsed sidebar)
export const IconLogoMark = ({
  size = 32,
  className = '',
  brandColor = '#0C1854',
  accentColor = '#118584',
  accentOpacity = 0.5,
  ...props
}) => (
  <svg
    role="img"
    aria-label="NetX"
    viewBox="0 0 32 34"
    width={size}
    height={size}
    className={className}
    {...props}
  >
    <defs>
      <clipPath id="nMarkClip">
        <rect x="0" y="0" width="32" height="34" />
      </clipPath>
    </defs>
    <g clipPath="url(#nMarkClip)">
      {/* Accent geometry (left radar lines from full logo, clipped) */}
      <g fill={accentColor} fillOpacity={accentOpacity}>
        <path d="M28.2 13.1 4.6 0H0v34.2h4V5.6l23.6 27.5h4.6V0h-4z" fill={brandColor} />
      </g>
    </g>
  </svg>
);

// Icons downloaded from https://iconsvg.xyz and modified to use currentColor
export const IconProfile = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const IconAdmin = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

export const IconLogout = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <path d="M10 3H6a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M19.8 12H9" />
  </svg>
);

export const IconFolder = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

export const IconBuilding = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

export const IconClipboard = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    <line x1="9" y1="12" x2="15" y2="12" />
    <line x1="9" y1="16" x2="13" y2="16" />
  </svg>
);

export const IconGlobe = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export const IconRefresh = ({ size = 24, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...baseProps} {...props}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
